import { getSmoothStepPath, Position, type XYPosition } from '@xyflow/react';
import type { MapEdgeWaypoint } from '../../lib/systemMap';

const PATH_OPTS = { borderRadius: 0, offset: 0 } as const;
const MIN_DRAGGABLE_SEGMENT = 20;

export type EdgeSegment = {
  index: number;
  orientation: 'horizontal' | 'vertical';
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  midX: number;
  midY: number;
  length: number;
};

function samePoint(a: XYPosition, b: XYPosition): boolean {
  return Math.abs(a.x - b.x) < 0.5 && Math.abs(a.y - b.y) < 0.5;
}

function dedupeConsecutive(points: XYPosition[]): XYPosition[] {
  return points.filter((p, i) => i === 0 || !samePoint(p, points[i - 1]));
}

/** Drop middle points that sit on the same horizontal or vertical line. */
export function simplifyCollinear(points: XYPosition[]): XYPosition[] {
  if (points.length <= 2) return points.map((p) => ({ ...p }));
  const result = [points[0]];
  for (let i = 1; i < points.length - 1; i += 1) {
    const prev = result[result.length - 1];
    const curr = points[i];
    const next = points[i + 1];
    const collinearH = Math.abs(prev.y - curr.y) < 0.5 && Math.abs(curr.y - next.y) < 0.5;
    const collinearV = Math.abs(prev.x - curr.x) < 0.5 && Math.abs(curr.x - next.x) < 0.5;
    if (!collinearH && !collinearV) result.push({ ...curr });
  }
  result.push(points[points.length - 1]);
  return dedupeConsecutive(result);
}

function parseStepPath(pathD: string): XYPosition[] {
  const points: XYPosition[] = [];
  const re = /([ML])\s*([-\d.]+)[,\s]+([-\d.]+)/gi;
  let match = re.exec(pathD);
  while (match) {
    points.push({ x: Number(match[2]), y: Number(match[3]) });
    match = re.exec(pathD);
  }
  return dedupeConsecutive(points);
}

function bendFromSource(from: XYPosition, to: XYPosition, sourcePosition: Position): XYPosition {
  if (sourcePosition === Position.Right || sourcePosition === Position.Left) {
    return { x: to.x, y: from.y };
  }
  return { x: from.x, y: to.y };
}

function bendToTarget(from: XYPosition, to: XYPosition, targetPosition: Position): XYPosition {
  if (targetPosition === Position.Left || targetPosition === Position.Right) {
    return { x: from.x, y: to.y };
  }
  return { x: to.x, y: from.y };
}

function expandCornersToPolyline(
  source: XYPosition,
  target: XYPosition,
  corners: XYPosition[],
  sourcePosition: Position,
  targetPosition: Position,
): XYPosition[] {
  const anchors = [source, ...corners, target];
  const result: XYPosition[] = [{ ...source }];

  for (let i = 1; i < anchors.length; i += 1) {
    const from = result[result.length - 1];
    const to = anchors[i];
    if (!samePoint(from, to) && from.x !== to.x && from.y !== to.y) {
      const isFirst = i === 1;
      const isLast = i === anchors.length - 1;
      if (isFirst) {
        result.push(bendFromSource(from, to, sourcePosition));
      } else if (isLast) {
        result.push(bendToTarget(from, to, targetPosition));
      } else {
        const prev = result.length >= 2 ? result[result.length - 2] : null;
        const prevHorizontal = prev != null && Math.abs(prev.y - from.y) < 0.5;
        result.push(prevHorizontal ? { x: from.x, y: to.y } : { x: to.x, y: from.y });
      }
    }
    if (!samePoint(result[result.length - 1], to)) {
      result.push({ ...to });
    }
  }

  return simplifyCollinear(result);
}

/** Shortest orthogonal polyline using React Flow step routing (no custom bends). */
export function getAutoPolylinePoints(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  sourcePosition: Position,
  targetPosition: Position,
): XYPosition[] {
  const [pathD] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    ...PATH_OPTS,
  });
  const parsed = parseStepPath(pathD);
  if (parsed.length >= 2) return simplifyCollinear(parsed);
  return [
    { x: sourceX, y: sourceY },
    { x: targetX, y: targetY },
  ];
}

export function resolveEdgePolyline(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  sourcePosition: Position,
  targetPosition: Position,
  corners: XYPosition[],
): XYPosition[] {
  const source = { x: sourceX, y: sourceY };
  const target = { x: targetX, y: targetY };
  if (corners.length === 0) {
    return getAutoPolylinePoints(sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition);
  }
  return expandCornersToPolyline(source, target, corners, sourcePosition, targetPosition);
}

export function polylineToPath(points: XYPosition[]): string {
  if (points.length === 0) return '';
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i += 1) {
    d += ` L ${points[i].x} ${points[i].y}`;
  }
  return d;
}

export function buildSystemMapEdgePath(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  waypoints: XYPosition[],
  sourcePosition: Position = Position.Right,
  targetPosition: Position = Position.Left,
): string {
  const polyline = resolveEdgePolyline(
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    waypoints,
  );
  return polylineToPath(polyline);
}

export function getDraggableSegments(points: XYPosition[]): EdgeSegment[] {
  const segments: EdgeSegment[] = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    const a = points[i];
    const b = points[i + 1];
    const dx = Math.abs(b.x - a.x);
    const dy = Math.abs(b.y - a.y);
    const horizontal = dy < 0.5;
    const vertical = dx < 0.5;
    if (!horizontal && !vertical) continue;

    const length = horizontal ? dx : dy;
    if (length < MIN_DRAGGABLE_SEGMENT) continue;

    segments.push({
      index: i,
      orientation: horizontal ? 'horizontal' : 'vertical',
      x1: a.x,
      y1: a.y,
      x2: b.x,
      y2: b.y,
      midX: (a.x + b.x) / 2,
      midY: (a.y + b.y) / 2,
      length,
    });
  }
  return segments;
}

/** Move a horizontal segment up/down or a vertical segment left/right (Whimsical-style). */
export function applySegmentDrag(
  points: XYPosition[],
  segmentIndex: number,
  value: number,
): XYPosition[] {
  if (segmentIndex < 0 || segmentIndex >= points.length - 1) return points.map((p) => ({ ...p }));

  const next = points.map((p) => ({ ...p }));
  const a = next[segmentIndex];
  const b = next[segmentIndex + 1];
  const horizontal = Math.abs(a.y - b.y) < 0.5;

  if (horizontal) {
    a.y = value;
    b.y = value;
  } else {
    a.x = value;
    b.x = value;
  }

  return simplifyCollinear(next);
}

export function extractCornerWaypoints(polyline: XYPosition[]): MapEdgeWaypoint[] {
  const simplified = simplifyCollinear(polyline);
  if (simplified.length <= 2) return [];
  return simplified.slice(1, -1).map((p) => ({ x: p.x, y: p.y }));
}

export function systemMapEdgeLabelPosition(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  waypoints: XYPosition[],
  sourcePosition: Position = Position.Right,
  targetPosition: Position = Position.Left,
): { x: number; y: number } {
  const polyline = resolveEdgePolyline(
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    waypoints,
  );
  const segments = getDraggableSegments(polyline);
  if (segments.length > 0) {
    const mid = segments[Math.floor(segments.length / 2)];
    return { x: mid.midX, y: mid.midY };
  }
  return { x: (sourceX + targetX) / 2, y: (sourceY + targetY) / 2 };
}
